import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Animated 3D Gantt / Schedule Visualizer
 * - Tracks of horizontal bars representing project activities
 * - Bars stagger-grow and pulse; critical bars glow amber
 * - Camera slowly drifts for a "live dashboard" feel
 */

const TRACKS = [
  { row: 0, start: -4.5, length: 2.4, critical: true,  delay: 0.0 },
  { row: 0, start: -1.5, length: 1.8, critical: true,  delay: 0.4 },
  { row: 0, start: 0.6,  length: 3.4, critical: true,  delay: 0.8 },
  { row: 1, start: -4.0, length: 1.6, critical: false, delay: 0.2 },
  { row: 1, start: -2.0, length: 2.6, critical: false, delay: 0.6 },
  { row: 1, start: 1.0,  length: 1.4, critical: true,  delay: 1.1 },
  { row: 2, start: -4.5, length: 1.2, critical: false, delay: 0.3 },
  { row: 2, start: -3.0, length: 2.0, critical: false, delay: 0.7 },
  { row: 2, start: -0.6, length: 1.4, critical: false, delay: 1.0 },
  { row: 2, start: 1.2,  length: 1.8, critical: false, delay: 1.4 },
  { row: 3, start: -4.2, length: 1.4, critical: true,  delay: 0.5 },
  { row: 3, start: -2.4, length: 1.6, critical: true,  delay: 0.9 },
  { row: 3, start: -0.4, length: 2.2, critical: true,  delay: 1.3 },
  { row: 4, start: -4.0, length: 1.0, critical: false, delay: 0.4 },
  { row: 4, start: -2.6, length: 1.8, critical: false, delay: 0.8 },
  { row: 4, start: -0.4, length: 1.6, critical: false, delay: 1.2 },
  { row: 4, start: 1.6,  length: 1.4, critical: false, delay: 1.6 },
];

function GanttBar({ row, start, length, critical, delay }) {
  const ref = useRef();
  const baseScale = useRef(0);
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    // staggered grow-in then sustained
    const phase = Math.max(0, Math.min(1, (t - delay) / 1.0));
    baseScale.current = phase;
    ref.current.scale.x = length * phase;
    // gentle pulse
    const pulse = 1 + Math.sin(t * 1.8 + start) * (critical ? 0.06 : 0.025);
    ref.current.scale.y = 0.35 * pulse;
    ref.current.scale.z = 0.35;
  });
  const color = critical ? "#FFB300" : "#00E5FF";
  const y = 2 - row * 0.9;
  const x = start + length / 2;
  return (
    <group position={[x, y, 0]}>
      <mesh ref={ref}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={critical ? 0.9 : 0.35}
          metalness={0.2}
          roughness={0.4}
        />
      </mesh>
    </group>
  );
}

function TrackLines() {
  const lines = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 5; i++) {
      const y = 2 - i * 0.9;
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-5.5, y, -0.2),
        new THREE.Vector3(5.5, y, -0.2),
      ]);
      arr.push(geo);
    }
    return arr;
  }, []);
  return (
    <group>
      {lines.map((g, i) => (
        <line key={i} geometry={g}>
          <lineBasicMaterial color="#1E2A3B" transparent opacity={0.6} />
        </line>
      ))}
    </group>
  );
}

function VerticalScans() {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.position.x = ((t * 0.6) % 11) - 5.5;
  });
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, -2.6, 0.2),
      new THREE.Vector3(0, 2.6, 0.2),
    ]);
    return g;
  }, []);
  return (
    <line ref={ref} geometry={geo}>
      <lineBasicMaterial color="#00E5FF" transparent opacity={0.55} />
    </line>
  );
}

function SceneGroup() {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.rotation.y = Math.sin(t * 0.18) * 0.18;
    ref.current.rotation.x = -0.18 + Math.sin(t * 0.12) * 0.04;
  });
  return (
    <group ref={ref}>
      <TrackLines />
      <VerticalScans />
      {TRACKS.map((b, i) => (
        <GanttBar key={i} {...b} />
      ))}
    </group>
  );
}

export default function ScheduleVisualizer() {
  return (
    <Canvas
      data-testid="schedule-3d-canvas"
      camera={{ position: [0, 0, 8], fov: 50 }}
      dpr={[1, 1.5]}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent" }}
    >
      <ambientLight intensity={0.7} />
      <pointLight position={[5, 5, 5]} intensity={0.6} color="#00E5FF" />
      <pointLight position={[-5, -3, 4]} intensity={0.4} color="#FFB300" />
      <SceneGroup />
    </Canvas>
  );
}
